import React, { useState } from 'react';
import { Scale, Save, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CrearRegistroFisicoDto } from '../../types/jugador';
import { useRegistroFisico } from '../../hooks/useRegistroFisico';
import { useJugadoresEquipoActivo } from '../../hooks/useJugadoresEquipoActivo';

interface ModalRegistroFisicoProps {
  isOpen: boolean;
  onClose: () => void;
  jugadorId: number;
  onRegistroGuardado: () => void;
}

/**
 * Modal para agregar un nuevo registro físico (altura y peso) de un jugador
 */
const ModalRegistroFisico: React.FC<ModalRegistroFisicoProps> = ({
  isOpen,
  onClose,
  jugadorId,
  onRegistroGuardado
}) => {
  const [altura, setAltura] = useState('');
  const [peso, setPeso] = useState('');
  const [errors, setErrors] = useState<{altura?: string; peso?: string}>({});
  const { crearRegistro, loading, error } = useRegistroFisico();
  const { temporada } = useJugadoresEquipoActivo();

  /**
   * Maneja el guardado del registro físico
   */
  const handleGuardar = async () => {
    // Validación
    const newErrors: {altura?: string; peso?: string} = {};
    
    if (!altura || Number(altura) <= 0) {
      newErrors.altura = 'La altura debe ser mayor a 0';
    }
    
    if (!peso || Number(peso) <= 0) {
      newErrors.peso = 'El peso debe ser mayor a 0';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      const registroData: CrearRegistroFisicoDto = {
        jugadorId,
        altura: Number(altura),
        peso: Number(peso),
        fechaRegistro: new Date().toISOString().split('T')[0], // Fecha actual en formato YYYY-MM-DD
        temporadaId: temporada?.id || 1 // ID de la temporada activa
      };

      const resultado = await crearRegistro(jugadorId, registroData);
      
      if (resultado) {
        // Limpiar formulario
        setAltura('');
        setPeso('');
        setErrors({});
        
        // Notificar que se guardó el registro
        onRegistroGuardado();
      }
      
    } catch (error) {
      console.error('Error al guardar registro físico:', error);
    }
  };

  /**
   * Maneja el cierre del modal
   */
  const handleCerrar = () => {
    if (!loading) {
      setAltura('');
      setPeso('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleCerrar} 
      size="sm"
    >
      <div className="space-y-6">
        {/* Header del Modal */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-700">
          <h2 className="text-xl font-bold text-slate-100 font-poppins">
            Nuevo Registro Físico
          </h2>
          <button
            onClick={handleCerrar}
            disabled={loading}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        <div className="flex items-center justify-center mb-6">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <Scale className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Mostrar error del hook si existe */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm font-poppins">
                {error}
              </p>
            </div>
          )}
          
          <Input
            label="Altura (cm)"
            type="number"
            value={altura}
            onChange={(e) => setAltura(e.target.value)}
            placeholder="Ej: 175"
            error={errors.altura}
            disabled={loading}
          />
          
          <Input
            label="Peso (kg)"
            type="number"
            value={peso}
            onChange={(e) => setPeso(e.target.value)}
            placeholder="Ej: 70"
            error={errors.peso}
            disabled={loading}
          />
        </div>
        
        <div className="text-sm text-slate-400 font-poppins text-center">
          El registro se guardará con la fecha actual
        </div>
        
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            onClick={handleCerrar}
            disabled={loading}
            className="flex-1"
            icon={<X />}
          >
            Cancelar
          </Button>
          
          <Button
            variant="primary"
            onClick={handleGuardar}
            disabled={loading}
            className="flex-1"
            icon={loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Save />
            )}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalRegistroFisico;